import React, { ReactNode, useMemo } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import { startOfMonth, isSameMonth, addMonths } from 'date-fns';
import { FlexContainer, Text } from "../../shared/styles";
import { messages } from "./messages";

const MAX_MONTH_COUNT = 24;

interface Props {
  selectedDate: Date;
  onClick: (date: Date) => void
}

const isSelectedDate = (currentDate: Date, selectedDate: Date) => {
  return isSameMonth(currentDate, selectedDate);
};

const getDateList = ({ selectedDate, onClick }: Props): ReactNode[] => {
  const result: ReactNode[] = [];
  for (let idx = 2; idx < MAX_MONTH_COUNT; idx++) {
    const date = addMonths(startOfMonth(new Date()), idx);
    const isSelected = isSelectedDate(date, selectedDate);
    result.push((
      <Text
        key={idx}
        className={isSelected ? "selected" : ""}
        selected={isSelected}
        onClick={() => !isSelected && onClick(date)}
      >
        <FormattedDate
          value={date}
          month="long"
          year="numeric"
        />
      </Text>
    ));
  }
  return result;
};

export const CurrentMonth = ({ selectedDate, onClick }: Props): JSX.Element => {
  const currentMonth = startOfMonth(new Date());
  const nextMonth = addMonths(currentMonth, 1);

  const isCurrentMonthSelected = useMemo(() =>
    isSelectedDate(currentMonth, selectedDate),
    [currentMonth, selectedDate]
  );
  const isNextMonthSelected = useMemo(() =>
    isSelectedDate(nextMonth, selectedDate),
    [selectedDate, nextMonth]
  );

  return (
    <FlexContainer>
      <Text
        key={0}
        className={isCurrentMonthSelected ? "selected" : ""}
        selected={isCurrentMonthSelected}
        onClick={() => onClick(new Date())}
      >
        <FormattedMessage {...messages.currentMonth} />
      </Text>
      <Text
        key={1}
        className={isNextMonthSelected ? "selected" : ""}
        selected={isNextMonthSelected}
        onClick={() => onClick(nextMonth)}
      >
        <FormattedMessage {...messages.nextMonth} />
      </Text>
      {getDateList({ selectedDate, onClick })
        .map(date => date)}
    </FlexContainer>
  );
};